/**
 * Environment configuration.
 *
 * Parsed and validated once at module load so a misconfigured deployment fails
 * immediately and loudly, rather than surfacing as a confusing runtime error
 * deep inside a network call.
 *
 * SECURITY: everything here originates from `VITE_*` variables, which Vite
 * inlines into the client bundle. These values are public by construction.
 * Nothing secret may be read through this module.
 */

export type AppEnvironment = 'development' | 'staging' | 'production';

export interface AppConfig {
  /** Absolute URL or same-origin path the API layer prefixes onto requests. */
  readonly apiBaseUrl: string;
  /** Display name for the application shell and document title. */
  readonly appName: string;
  /** Deployment environment; drives logging verbosity and diagnostics. */
  readonly environment: AppEnvironment;
  /** Per-request timeout in milliseconds. */
  readonly apiTimeoutMs: number;
  /**
   * Serve API responses from in-memory fixtures instead of the network.
   * Set while a feature's backend endpoints do not exist yet; the affected
   * `injectEndpoints` calls are otherwise unchanged, so removing this only
   * requires flipping the flag off.
   */
  readonly useMockApi: boolean;
  readonly features: {
    /** In-app developer diagnostics. Forced off in production builds. */
    readonly devtools: boolean;
    readonly analytics: boolean;
  };
  readonly isProduction: boolean;
  readonly isDevelopment: boolean;
}

class EnvironmentConfigError extends Error {
  override readonly name = 'EnvironmentConfigError';
}

function requireString(value: string | undefined, key: string): string {
  const trimmed = value?.trim();
  if (!trimmed) {
    throw new EnvironmentConfigError(
      `Missing required environment variable ${key}. Copy .env.example to .env.local and set it.`,
    );
  }
  return trimmed;
}

function optionalString(value: string | undefined, fallback: string): string {
  // An empty or whitespace-only value counts as unset, so `??` would not do:
  // it only replaces null/undefined and would let "" through.
  const trimmed = value?.trim();
  if (trimmed === undefined || trimmed === '') return fallback;
  return trimmed;
}

function parseBoolean(value: string | undefined, fallback: boolean): boolean {
  const trimmed = value?.trim().toLowerCase();
  if (trimmed === undefined || trimmed === '') return fallback;
  if (trimmed === 'true' || trimmed === '1') return true;
  if (trimmed === 'false' || trimmed === '0') return false;
  return fallback;
}

function parsePositiveInt(value: string | undefined, fallback: number, key: string): number {
  const trimmed = value?.trim();
  if (!trimmed) return fallback;

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed <= 0) {
    throw new EnvironmentConfigError(
      `Environment variable ${key} must be a positive integer, received "${trimmed}".`,
    );
  }
  return parsed;
}

function parseEnvironment(value: string | undefined, fallback: AppEnvironment): AppEnvironment {
  const trimmed = value?.trim().toLowerCase();
  if (!trimmed) return fallback;
  if (trimmed === 'development' || trimmed === 'staging' || trimmed === 'production') {
    return trimmed;
  }
  throw new EnvironmentConfigError(
    `Environment variable VITE_APP_ENV must be one of development | staging | production, received "${trimmed}".`,
  );
}

/**
 * Rejects API base URLs that would let a misconfigured deployment send
 * credentialed requests somewhere unintended, or downgrade to cleartext.
 */
function validateApiBaseUrl(rawUrl: string, isProduction: boolean): string {
  // A same-origin path (`/api`) is always safe: the browser resolves it
  // against the current origin, so it cannot leak to a third party.
  if (rawUrl.startsWith('/')) {
    return rawUrl.replace(/\/+$/, '');
  }

  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    throw new EnvironmentConfigError(
      `VITE_API_BASE_URL must be an absolute http(s) URL or a same-origin path beginning with "/", received "${rawUrl}".`,
    );
  }

  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    throw new EnvironmentConfigError(
      `VITE_API_BASE_URL must use http or https, received protocol "${parsed.protocol}".`,
    );
  }

  // Cleartext HTTP in production would expose session cookies and request
  // bodies on the wire. Localhost stays allowed for local backend work.
  const isLocalhost = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
  if (isProduction && parsed.protocol === 'http:' && !isLocalhost) {
    throw new EnvironmentConfigError(
      'VITE_API_BASE_URL must use https in production builds; plain http would expose credentials in transit.',
    );
  }

  return parsed.toString().replace(/\/+$/, '');
}

function loadConfig(): AppConfig {
  // Bracket access: Vite's own `ImportMetaEnv` carries an index signature, and
  // `noPropertyAccessFromIndexSignature` requires indexed reads to be explicit.
  const env = import.meta.env;
  const buildMode: AppEnvironment = env.PROD ? 'production' : 'development';
  const environment = parseEnvironment(env['VITE_APP_ENV'], buildMode);
  const isProduction = environment === 'production' || env.PROD;

  const apiBaseUrl = validateApiBaseUrl(
    requireString(env['VITE_API_BASE_URL'], 'VITE_API_BASE_URL'),
    isProduction,
  );

  return Object.freeze({
    apiBaseUrl,
    appName: optionalString(env['VITE_APP_NAME'], 'Primius.ai Agency'),
    environment,
    apiTimeoutMs: parsePositiveInt(env['VITE_API_TIMEOUT_MS'], 30_000, 'VITE_API_TIMEOUT_MS'),
    useMockApi: parseBoolean(env['VITE_USE_MOCK_API'], false),
    features: Object.freeze({
      // Diagnostics are never enabled in a production build, regardless of the
      // configured flag — an operator mistake should not expose internals.
      devtools: !isProduction && parseBoolean(env['VITE_ENABLE_DEVTOOLS'], false),
      analytics: parseBoolean(env['VITE_ENABLE_ANALYTICS'], false),
    }),
    isProduction,
    isDevelopment: !isProduction,
  });
}

export const config: AppConfig = loadConfig();

/** Exported for unit testing the parsing rules in isolation. */
export const __testing = {
  validateApiBaseUrl,
  parseBoolean,
  parsePositiveInt,
  parseEnvironment,
  requireString,
  EnvironmentConfigError,
};
